import { useQuery } from '@tanstack/react-query';
import { Column } from 'primereact/column';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import { useGetPaginatedCertificateTasks } from '../../../../../../../../hooks/api/certificates';
import { useCertificateTasksTableQueryParams } from '../../../../../../../../hooks/useCertificateTasksTableQueryParams';
import { Locale } from '../../../../../../../../i18n';
import { ANSWERING_CERTIFICATE_TASKS_QUERY_KEY } from '../../../../../../../../shared/helpers/answering-certificate-tasks';
import { getPaginatorTemplate } from '../../../../../../../../shared/helpers/get-paginator-template';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import {
  CertificateTaskStatus as CertificateTaskStatusEnum,
  ICertificateTask,
} from '../../../../../../../../shared/interfaces/ICertificateTask';
import AddAccessButton from '../add-access-button/AddAccessButton';
import CertificateTaskStatus from '../certificate-task-status/CertificateTaskStatus';
import CertificateTasksTableHeader from '../certificate-tasks-table-header/CertificateTasksTableHeader';
import styles from './CertificateTasksTable.module.css';

const CertificateTasksTable = (): JSX.Element => {
  const { setParams, skip, size, sort, filters } = useCertificateTasksTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const {
    i18n: { language },
    t,
  } = useTranslation();

  const { data, isLoading } = useGetPaginatedCertificateTasks({
    skip,
    size,
    sort,
    ...filters,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [data]);

  // Tasks whose answer is still being processed live in a dedicated cache entry (see
  // answering-certificate-tasks.ts) so the tasks invalidation never wipes them. Reading them
  // through useQuery is what makes the table re-render on setQueryData.
  const { data: answeringTaskIds = [] } = useQuery({
    queryKey: ANSWERING_CERTIFICATE_TASKS_QUERY_KEY,
    queryFn: (): number[] => {
      return [];
    },
    enabled: false,
    initialData: [],
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const answeringTaskIdSet = useMemo(() => {
    return new Set(answeringTaskIds);
  }, [answeringTaskIds]);

  const companyTemplate = (rowData: ICertificateTask): JSX.Element => {
    if (!rowData.company.companyName) {
      return (
        <span className={`text-sm font-light text-gray-700 ${styles['company-bpn']}`}>
          {rowData.company.companyBpn}
        </span>
      );
    }

    return (
      <div className='flex flex-column gap-1'>
        <span>{rowData.company.companyName}</span>
        <span className={`text-sm font-light text-gray-700 ${styles['company-bpn']}`}>
          {rowData.company.companyBpn}
        </span>
      </div>
    );
  };

  const certificateTypeTemplate = (rowData: ICertificateTask): JSX.Element => {
    return <span>{rowData.certificateType}</span>;
  };

  const sitesRequestedTemplate = (rowData: ICertificateTask): React.ReactNode => {
    if (!rowData.sitesRequested?.length) {
      return <span className='text-gray-700'>-</span>;
    }

    return (
      <div className='flex flex-column gap-1'>
        {rowData.sitesRequested.map((site) => {
          return (
            <span key={site.bpn} className={styles['company-bpn']}>
              {site.bpn}
            </span>
          );
        })}
      </div>
    );
  };

  const receivedTemplate = (rowData: ICertificateTask): React.ReactNode => {
    return parseDateWithoutTime(rowData.dateReceived, language as Locale);
  };

  const statusTemplate = (rowData: ICertificateTask): JSX.Element => {
    if (answeringTaskIdSet.has(rowData.id)) {
      return <Skeleton height='1rem' width='80%' />;
    }

    return <CertificateTaskStatus status={rowData.status} />;
  };

  const addAccessTemplate = (rowData: ICertificateTask): JSX.Element | null => {
    // Checked before the status test on purpose: the answer is still in flight, so the cached row
    // is also still Pending and would otherwise keep offering a button that grants access twice.
    if (answeringTaskIdSet.has(rowData.id)) {
      return <Skeleton height='2.25rem' width='7rem' />;
    }

    if (rowData.status === CertificateTaskStatusEnum.Pending) {
      return <AddAccessButton certificateTask={rowData} />;
    }

    return null;
  };

  const isTableEmpty =
    !isLoading &&
    (!data || data?.data?.length === 0) &&
    Object.values(filters).filter((value) => {
      return Boolean(value);
    }).length === 0;

  if (isTableEmpty) {
    return (
      <EmptyScreen>
        <p className='flex text-center font-light'>
          {t('tasksPage.tabs.certificateTasksTab.emptyScreenText')}
        </p>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['certificate-tasks-table']}>
      <DataTable
        value={data?.data}
        scrollable
        loading={isLoading}
        lazy
        header={<CertificateTasksTableHeader />}
        rows={size}
        paginatorPosition='top'
        paginator
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '1200px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          header={t('tasksPage.tabs.certificateTasksTab.certificateTasksTableColumns.company')}
          body={companyTemplate}
          sortable
          sortField='company'
          style={{ width: '25%', wordBreak: 'break-word' }}
        />

        <Column
          header={t(
            'tasksPage.tabs.certificateTasksTab.certificateTasksTableColumns.certificateType',
          )}
          body={certificateTypeTemplate}
          sortable
          sortField='certificateType'
          style={{ width: '18%', wordBreak: 'break-word' }}
        />

        <Column
          header={t(
            'tasksPage.tabs.certificateTasksTab.certificateTasksTableColumns.sitesRequested',
          )}
          body={sitesRequestedTemplate}
          style={{ width: '20%', wordBreak: 'break-word' }}
        />

        <Column
          header={t('tasksPage.tabs.certificateTasksTab.certificateTasksTableColumns.received')}
          body={receivedTemplate}
          sortable
          sortField='dateReceived'
          style={{ width: '12%' }}
        />

        <Column
          header={t('tasksPage.tabs.certificateTasksTab.certificateTasksTableColumns.status')}
          body={statusTemplate}
          sortable
          sortField='status'
          style={{ width: '12%' }}
        />

        <Column body={addAccessTemplate} style={{ width: '13%' }} />
      </DataTable>
    </div>
  );
};

export default CertificateTasksTable;
